import { useQuery } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import useHttpClient from './useHttpClient';

export interface IDocument {
  documentId: {
    native: number;
    partner: number | null;
  };
  filename: string,
  size: number,
  projectId: {
    native: number;
    partner?: number;
  };
  uploaderId: {
    native: number;
    partner: number | null;
  };
  folderId: {
    native: number;
    partner: number | null;
  };
  folderName?: string;
  uploadDate: string,
  hashtags: string[],
  uploaderFullname: string,
}

interface IProjectDocuments {
  count: number;
  offset: number;
  limit: number;
  hasMore: boolean;
  requestedFields: string;
  items: IDocument[];
}

interface IProjectDocumentPayload {
  called: boolean;
  loading: boolean;
  error?: Error;
  documents?: IProjectDocuments;
}

export interface IQueryProjectDocumentsHook {
  projectDocumentsSearchResult: IProjectDocumentPayload;
  getProjectDocuments: (projectId: number, searchTerm: string, folderId?: string) => void;
}

interface IQueryParams {
  projectId: number;
  searchTerm: string;
  folderId: string;
}

const useQueryProjectDocuments = (): IQueryProjectDocumentsHook => {
  const httpClient = useHttpClient();
  const [queryParams, setQueryParams] = useState<IQueryParams | null>(null);

  const {
    data: documents,
    error,
    isLoading: loading,
    isFetched,
  } = useQuery<IProjectDocuments>({
    queryKey: ['projectDocuments', queryParams?.projectId, queryParams?.searchTerm, queryParams?.folderId],
    queryFn: async () => {
      if (!queryParams) return undefined;

      const { projectId, searchTerm, folderId } = queryParams;
      const encodedSearchTerm = encodeURIComponent(searchTerm);
      const url = `/projects/${projectId}/documents?searchTerm=${encodedSearchTerm}&folderId=${folderId}`;

      const response = await httpClient.get(url);
      return response.data;
    },
    enabled: !!queryParams,
  });

  const getProjectDocuments = useCallback(
    (projectId: number, searchTerm: string, folderId: string = '') => {
      setQueryParams({ projectId, searchTerm, folderId });
    }, []
  );

  return {
    projectDocumentsSearchResult: {
      called: isFetched,
      error: error as Error | undefined,
      loading,
      documents,
    },
    getProjectDocuments,
  };
};

export default useQueryProjectDocuments;
